import { SubmitSuccessAction } from '@wix/ambassador-forms-v4-form/types';
import { submitFormClick as submitFormBi } from '@wix/bi-logger-forms-data/v2';
import { EXPERIMENTS, useBi, useExperiments, usePanorama, useSubmitSuccessTrigger, useTranslation } from '@wix/form-fields';
import { useCallback } from 'react';
import { useSubmitStatus } from '../../contexts/submit-status-context';
import { INTERACTIONS } from '../../features/monitoring/interactions';
import { BiFormSubmissionStatus } from '../../types/bi';
import { getCheckoutMessage, getErrorCode, getErrorList, getFieldCustomErrorMessages, getFormCustomErrorMessage, redirectToExternalUrl } from './utils';
export const useSubmit = _ref => {
  let {
    validateAndScrollToError,
    submitAction,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    resetFormValues,
    values,
    submitSettings,
    handleStepChange,
    navigateToCheckout,
    openPopup,
    onValidate,
    handleScrollToFirstError
  } = _ref;
  const {
    setSubmitSuccessMessage,
    setSubmitFailureCode,
    setSubmitFailureMessage
  } = useSubmitStatus();
  const {
    t
  } = useTranslation();
  const {
    report
  } = useBi();
  const fireSubmitSuccess = useSubmitSuccessTrigger();
  const panorama = usePanorama();
  const {
    isExperimentEnabled
  } = useExperiments();
  const focusOnSubmitSuccess = !isExperimentEnabled(EXPERIMENTS.SUBMIT_SUCCESS_NO_FOCUS);
  const reportSubmitFormBi = useCallback(_ref2 => {
    let {
      status,
      id
    } = _ref2;
    return report(submitFormBi({
      submission_status: status,
      submission_id: id ?? undefined
    }));
  }, [report]);
  const handleSubmitSuccess = useCallback(submission => {
    var _submission$orderDeta;
    const action = submitSettings == null ? void 0 : submitSettings.submitSuccessAction;
    const checkoutId = submission == null || (_submission$orderDeta = submission.orderDetails) == null ? void 0 : _submission$orderDeta.checkoutId;
    if (checkoutId && navigateToCheckout) {
      setSubmitSuccessMessage(getCheckoutMessage(t('submit.checkout.message')));
      navigateToCheckout(checkoutId);
    } else if (action === SubmitSuccessAction.THANK_YOU_MESSAGE) {
      var _submitSettings$thank, _submitSettings$thank2;
      setSubmitSuccessMessage(submitSettings == null || (_submitSettings$thank = submitSettings.thankYouMessageOptions) == null ? void 0 : _submitSettings$thank.richContent);
      if (submitSettings != null && (_submitSettings$thank2 = submitSettings.thankYouMessageOptions) != null && _submitSettings$thank2.durationInSeconds) {
        var _submitSettings$thank3;
        setTimeout(() => {
          setSubmitSuccessMessage(undefined);
          if (focusOnSubmitSuccess) {
            handleStepChange(0);
          }
        }, (submitSettings == null || (_submitSettings$thank3 = submitSettings.thankYouMessageOptions) == null ? void 0 : _submitSettings$thank3.durationInSeconds) * 1000);
      } else if (focusOnSubmitSuccess) {
        handleStepChange(0);
      }
    } else if (action === SubmitSuccessAction.REDIRECT) {
      redirectToExternalUrl(submitSettings == null ? void 0 : submitSettings.redirectOptions);
    } else if (action === SubmitSuccessAction.POPUP) {
      setTimeout(() => {
        var _submitSettings$popup;
        openPopup == null || openPopup((submitSettings == null || (_submitSettings$popup = submitSettings.popupOptions) == null ? void 0 : _submitSettings$popup.popupId) ?? '', {});
      }, 500);
    }
    setSubmitFailureCode(undefined);
    setSubmitFailureMessage(undefined);
  }, [t, submitSettings, handleStepChange, focusOnSubmitSuccess, navigateToCheckout, openPopup, setSubmitSuccessMessage, setSubmitFailureCode, setSubmitFailureMessage]);
  const handleSubmitFailure = useCallback(error => {
    const err = getErrorCode(error);
    setSubmitFailureCode(err);
    setSubmitSuccessMessage(undefined);
  }, [setSubmitFailureCode, setSubmitSuccessMessage]);
  const handleCustomErrorMessages = useCallback(error => {
    const errors = getErrorList(error);
    const formCustomErrorMessage = getFormCustomErrorMessage(errors);
    if (formCustomErrorMessage) {
      setSubmitFailureMessage(formCustomErrorMessage);
    }
    const fieldCustomErrorMessages = getFieldCustomErrorMessages(errors);
    if (fieldCustomErrorMessages != null && fieldCustomErrorMessages.length) {
      onValidate(fieldCustomErrorMessages);
      handleScrollToFirstError == null || handleScrollToFirstError(fieldCustomErrorMessages);
    }
  }, [setSubmitFailureMessage, onValidate, handleScrollToFirstError]);
  const submitForm = useCallback(async () => {
    const errors = await validateAndScrollToError(values);
    onSubmit == null || onSubmit();
    reportSubmitFormBi({
      status: BiFormSubmissionStatus.INIT
    });
    if (errors != null && errors.length) {
      reportSubmitFormBi({
        status: BiFormSubmissionStatus.VALIDATION_FAILURE
      });
      return;
    }
    let response;
    try {
      panorama == null || panorama.transaction(INTERACTIONS.SUBMIT_FORM).start();
      response = await submitAction(values);
    } catch (error) {
      onSubmitFailure == null || onSubmitFailure(error);
      reportSubmitFormBi({
        status: BiFormSubmissionStatus.FAILURE
      });
      handleCustomErrorMessages(error);
      handleSubmitFailure(error);
      throw error;
    }
    if (response) {
      onSubmitSuccess == null || onSubmitSuccess(response);
      reportSubmitFormBi({
        status: BiFormSubmissionStatus.SUCCESS,
        id: response.id
      });
      panorama == null || panorama.transaction(INTERACTIONS.SUBMIT_FORM).finish();
      fireSubmitSuccess({
        ...values
      });
      handleSubmitSuccess(response);
      resetFormValues == null || resetFormValues();
    }
    return response;
  }, [validateAndScrollToError, values, onSubmit, reportSubmitFormBi, panorama, submitAction, onSubmitFailure, handleCustomErrorMessages, handleSubmitFailure, onSubmitSuccess, fireSubmitSuccess, handleSubmitSuccess, resetFormValues]);
  return {
    submitForm
  };
};
//# sourceMappingURL=use-submit.js.map